import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  FileText,
  Lock,
  Loader2,
  Sparkles,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CountrySelect } from "@/components/ui/country-select";
import { useToast } from "@/hooks/use-toast";
import {
  generateInnovationBlueprint,
  type LeadFormData,
  type BlueprintAnswers,
} from "@/lib/leadService";
import { trackFormSubmission, trackConversion, trackBlueprintSubmit } from "@/lib/analytics";
import { detectCountryFromTimezone, getCountryByCode } from "@/lib/countries";
import HeroFields from "@/components/HeroFields";

const timelineOptions = [
  { value: "4-weeks", label: "4 weeks - first prototype" },
  { value: "8-weeks", label: "8 weeks - market-ready MVP" },
  { value: "12-weeks", label: "12+ weeks - full program" },
  { value: "not-sure", label: "Not sure - need guidance" },
];

const aiMaturityOptions = [
  { value: "exploring", label: "Exploring - no live AI projects yet" },
  { value: "piloting", label: "Piloting - one or two experiments" },
  { value: "scaling", label: "Scaling - need to industrialize" },
  { value: "optimizing", label: "Optimizing - mature, looking for edge" },
];

const totalSteps = 3;

const InnovationBlueprint = () => {
  const { toast } = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    fullName: "",
    company: "",
    email: "",
    industry: "",
    country: "",
  });
  const [blueprint, setBlueprint] = useState<BlueprintAnswers>({
    challenge: "",
    timeline: "",
    aiMaturity: "",
    blocker: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  useEffect(() => {
    setForm((p) => ({ ...p, country: detectCountryFromTimezone() }));
  }, []);

  const stepErrors = (target: number): Record<string, string> => {
    const e: Record<string, string> = {};
    if (target === 1) {
      if (!form.fullName.trim()) e.fullName = "Required";
      if (!form.company.trim()) e.company = "Required";
      if (!form.email.trim()) e.email = "Required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email";
      if (!form.industry.trim()) e.industry = "Required";
      if (!form.country) e.country = "Required";
    }
    if (target === 2) {
      if (!blueprint.challenge.trim()) e.challenge = "Required";
      if (!blueprint.timeline) e.timeline = "Required";
    }
    if (target === 3) {
      if (!blueprint.aiMaturity) e.aiMaturity = "Required";
      if (!blueprint.blocker.trim()) e.blocker = "Required";
      if (!agreed) e.agreed = "Please accept to continue";
    }
    return e;
  };

  const validate = () => {
    const e = { ...stepErrors(1), ...stepErrors(2), ...stepErrors(3) };
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goToStep = (target: number) => {
    if (target > step) {
      const e = stepErrors(step);
      setErrors(e);
      if (Object.keys(e).length > 0) return;
    }
    setStep(target);
    setErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);

    const countryName = getCountryByCode(form.country)?.name || form.country;
    const leadData: LeadFormData = {
      fullName: form.fullName,
      companyName: form.company,
      email: form.email,
      phone: "Not provided",
      country: countryName,
      industry: form.industry,
      message: `Requested a personalized Innovation Blueprint. Challenge: ${blueprint.challenge}. Timeline: ${blueprint.timeline}. AI maturity: ${blueprint.aiMaturity}. Blocker: ${blueprint.blocker}.`,
      pageSource: "Innovation Blueprint AI",
      selectedPath: null,
    };

    const result = await generateInnovationBlueprint(leadData, blueprint, honeypot);
    setIsSubmitting(false);

    if (result.success && result.pdf_base64) {
      trackFormSubmission("blueprint_form", true);
      trackConversion("blueprint_request");
      trackBlueprintSubmit(form.industry, blueprint.timeline, blueprint.aiMaturity);

      // Convert base64 PDF to a downloadable blob URL
      const byteCharacters = atob(result.pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setDownloadUrl(url);

      // Trigger automatic download
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename || "Skandor-Innovation-Blueprint.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setIsSubmitted(true);
      toast({
        title: "Your blueprint is ready",
        description: "We've generated a personalized guide and started your download.",
      });
    } else if (result.success && result.qaMode) {
      trackFormSubmission("blueprint_form", true);
      setIsSubmitted(true);
      toast({
        title: "QA mode: blueprint request validated",
        description: "No PDF was generated because this is a test email.",
      });
    } else {
      trackFormSubmission("blueprint_form", false);
      toast({
        title: "Error",
        description: result.error || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <SEOHead
        title="Innovation Blueprint | Skandor Technologies"
        description="A practical, AI-generated blueprint for shipping AI-driven, market-ready products in 4–8 weeks. Request your personalized copy."
        canonicalPath="/resources/innovation-blueprint"
      />
      <Navbar />
      <main id="main-content" tabIndex={-1} className="min-h-screen focus:outline-none">
  
        <section className="relative overflow-hidden bg-tint pb-20 pt-32">
          <HeroFields compact />
          <div className="container relative z-10 mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">
              {/* Left: pitch */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-tint border border-line text-steel-2 text-xs font-semibold mb-5">
                  <Sparkles className="w-3.5 h-3.5" /> AI-Generated
                </div>
                <h1 className="text-[clamp(2rem,5vw,3.25rem)] font-semibold text-foreground mb-5">
                  The Skandor <span className="text-steel-2">Innovation Blueprint</span>
                </h1>
                <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                  Answer four short questions and our AI will build a tailored blueprint for
                  validating, scoping, and shipping your AI-driven product in 4–8 weeks —
                  without bloated teams or slide decks.
                </p>
                <ul className="space-y-3 text-base text-ink-2">
                  {[
                    "Executive summary written for your specific challenge and timeline",
                    "A 4–8 week roadmap with week-by-week focus and deliverables",
                    "Risk register and decision checklist for your maturity level",
                    "Immediate next steps you can act on this week",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
  
              {/* Right: form */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              >
                {isSubmitted ? (
                  <div className="surface-card p-10 shadow-[var(--shadow-card)] text-center">
                    <div className="inline-flex p-4 rounded-full bg-success/10 mb-5">
                      <CheckCircle2 className="w-10 h-10 text-success" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-3">Your blueprint is ready.</h3>
                    <p className="text-base text-muted-foreground mb-6">
                      The download should have started automatically. We've also sent a copy to{" "}
                      <span className="text-foreground font-medium">{form.email}</span>.
                    </p>
                    {downloadUrl && (
                      <Button asChild variant="primary" size="lg" className="w-full">
                        <a
                          href={downloadUrl}
                          download={resultFilename(form.company)}
                        >
                          <FileText className="w-4 h-4" />
                          Download the blueprint (PDF)
                        </a>
                      </Button>
                    )}
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    className="surface-card p-8 shadow-[var(--shadow-card)]"
                    aria-labelledby="bp-form-title"
                  >
                    <div className="flex items-baseline justify-between mb-2 gap-3">
                      <h3 id="bp-form-title" className="text-base font-semibold text-foreground">
                        Generate your blueprint
                      </h3>
                      <span
                        className="font-mono text-xs uppercase tracking-[0.18em] text-steel-2 font-semibold"
                        aria-live="polite"
                      >
                        Step {step} of {totalSteps}
                      </span>
                    </div>
  
                    {/* Progress bar */}
                    <div
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={totalSteps}
                      aria-valuenow={step}
                      aria-label={`Blueprint form step ${step} of ${totalSteps}`}
                      className="w-full h-1.5 rounded-full bg-tint mb-4 overflow-hidden"
                    >
                      <div
                        className="h-full bg-grad-brand transition-all duration-500"
                        style={{ width: `${(step / totalSteps) * 100}%` }}
                      />
                    </div>
  
                    <p className="text-sm text-muted-foreground mb-6">
                      {step === 1 && "About you — so we can tailor the blueprint to your context."}
                      {step === 2 && "About the challenge — what you want the blueprint to solve."}
                      {step === 3 && "About the constraints — so the roadmap is realistic."}
                    </p>
  
                    {/* Honeypot */}
                    <div className="absolute -left-[9999px]" aria-hidden="true">
                      <Input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                      />
                    </div>
  
                    <div className="space-y-5">
                      {step === 1 && (
                        <>
                          <div className="space-y-2">
                            <Label htmlFor="bp-name">Full Name *</Label>
                            <Input
                              id="bp-name"
                              value={form.fullName}
                              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                              className={`min-h-11 ${errors.fullName ? "border-destructive" : ""}`}
                              placeholder="Jane Doe"
                            />
                            {errors.fullName && <p className="text-destructive text-sm">{errors.fullName}</p>}
                          </div>
  
                          <div className="space-y-2">
                            <Label htmlFor="bp-company">Company *</Label>
                            <Input
                              id="bp-company"
                              value={form.company}
                              onChange={(e) => setForm({ ...form, company: e.target.value })}
                              className={`min-h-11 ${errors.company ? "border-destructive" : ""}`}
                              placeholder="Your company"
                            />
                            {errors.company && <p className="text-destructive text-sm">{errors.company}</p>}
                          </div>
  
                          <div className="space-y-2">
                            <Label htmlFor="bp-email">Work email *</Label>
                            <Input
                              id="bp-email"
                              type="email"
                              value={form.email}
                              onChange={(e) => setForm({ ...form, email: e.target.value })}
                              className={`min-h-11 ${errors.email ? "border-destructive" : ""}`}
                              placeholder="jane@company.com"
                            />
                            {errors.email && <p className="text-destructive text-sm">{errors.email}</p>}
                          </div>
  
                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="bp-industry">Industry *</Label>
                              <Input
                                id="bp-industry"
                                value={form.industry}
                                onChange={(e) => setForm({ ...form, industry: e.target.value })}
                                className={`min-h-11 ${errors.industry ? "border-destructive" : ""}`}
                                placeholder="e.g. Healthcare"
                              />
                              {errors.industry && <p className="text-destructive text-sm">{errors.industry}</p>}
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="bp-country">Country *</Label>
                              <CountrySelect
                                id="bp-country"
                                value={form.country}
                                onValueChange={(v) => setForm({ ...form, country: v })}
                                error={errors.country}
                              />
                            </div>
                          </div>
                        </>
                      )}
  
                      {step === 2 && (
                        <>
                          <div className="space-y-2">
                            <Label htmlFor="bp-challenge">
                              What is the biggest product or operations challenge you want to solve? *
                            </Label>
                            <textarea
                              id="bp-challenge"
                              rows={4}
                              value={blueprint.challenge}
                              onChange={(e) => setBlueprint({ ...blueprint, challenge: e.target.value })}
                              className={`w-full rounded-md border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                                errors.challenge ? "border-destructive" : "border-input"
                              }`}
                              placeholder="e.g. We need to reduce clinical intake time without adding headcount."
                            />
                            {errors.challenge && <p className="text-destructive text-sm">{errors.challenge}</p>}
                          </div>
  
                          <div className="space-y-2">
                            <Label>When do you need a first result? *</Label>
                            <div className="grid md:grid-cols-2 gap-2">
                              {timelineOptions.map((opt) => (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => setBlueprint({ ...blueprint, timeline: opt.value })}
                                  className={`text-left px-3 py-2 min-h-11 rounded-md border text-sm transition-colors ${
                                    blueprint.timeline === opt.value
                                      ? "border-primary bg-primary/10 text-primary font-medium"
                                      : "border-input bg-background hover:bg-tint"
                                  }`}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                            {errors.timeline && <p className="text-destructive text-sm">{errors.timeline}</p>}
                          </div>
                        </>
                      )}
  
                      {step === 3 && (
                        <>
                          <div className="space-y-2">
                            <Label>How would you describe your current AI / data maturity? *</Label>
                            <div className="grid md:grid-cols-2 gap-2">
                              {aiMaturityOptions.map((opt) => (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => setBlueprint({ ...blueprint, aiMaturity: opt.value })}
                                  className={`text-left px-3 py-2 min-h-11 rounded-md border text-sm transition-colors ${
                                    blueprint.aiMaturity === opt.value
                                      ? "border-primary bg-primary/10 text-primary font-medium"
                                      : "border-input bg-background hover:bg-tint"
                                  }`}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                            {errors.aiMaturity && <p className="text-destructive text-sm">{errors.aiMaturity}</p>}
                          </div>
  
                          <div className="space-y-2">
                            <Label htmlFor="bp-blocker">
                              What is the single biggest blocker right now? *
                            </Label>
                            <textarea
                              id="bp-blocker"
                              rows={3}
                              value={blueprint.blocker}
                              onChange={(e) => setBlueprint({ ...blueprint, blocker: e.target.value })}
                              className={`w-full rounded-md border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                                errors.blocker ? "border-destructive" : "border-input"
                              }`}
                              placeholder="e.g. We don't know which use case to prioritise."
                            />
                            {errors.blocker && <p className="text-destructive text-sm">{errors.blocker}</p>}
                          </div>
  
                          {/* Data-use disclosure */}
                          <div className="rounded-md bg-tint border border-border p-4 text-sm text-ink-2 leading-relaxed">
                            <p className="font-semibold text-ink mb-1 flex items-center gap-1.5">
                              <Lock className="w-3.5 h-3.5" aria-hidden="true" /> How we use your answers
                            </p>
                            <p>
                              Your responses generate one personalized PDF that we email to you.
                              We do not sell your data, do not add you to marketing sequences,
                              and store your submission only to follow up if you request it. See
                              our{" "}
                              <a href="/privacy" className="text-steel-2 font-semibold hover:underline underline-offset-4">
                                Privacy Policy
                              </a>.
                            </p>
                          </div>
  
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id="bp-agreed"
                              checked={agreed}
                              onCheckedChange={(c) => setAgreed(c as boolean)}
                            />
                            <Label
                              htmlFor="bp-agreed"
                              className="text-sm text-muted-foreground font-normal leading-relaxed"
                            >
                              I agree to the{" "}
                              <a href="/privacy" className="text-steel-2 font-semibold hover:underline underline-offset-4">Privacy Policy</a>{" "}
                              and to receive the blueprint by email.
                            </Label>
                          </div>
                          {errors.agreed && <p className="text-destructive text-sm">{errors.agreed}</p>}
                        </>
                      )}
  
                      <div className="flex gap-3 pt-2">
                        {step > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="lg"
                            className="min-h-11"
                            onClick={() => goToStep(step - 1)}
                            disabled={isSubmitting}
                          >
                            Back
                          </Button>
                        )}
  
                        {step < totalSteps ? (
                          <Button
                            type="button"
                            variant="primary"
                            size="lg"
                            className="flex-1 min-h-11"
                            onClick={() => goToStep(step + 1)}
                          >
                            Continue
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            className="flex-1 min-h-11"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <span className="flex items-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Generating your blueprint…
                              </span>
                            ) : (
                              <>
                                Generate my blueprint
                                <ArrowRight className="w-4 h-4" />
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </form>
                )}
              </motion.div>
            </div>
          </div>
        </section>
  
      </main>
      <Footer />
    </>
  );
};

function resultFilename(company: string) {
  const safe = company.trim().replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-]/g, "");
  return safe
    ? `Skandor-Innovation-Blueprint-${safe}.pdf`
    : "Skandor-Innovation-Blueprint.pdf";
}

export default InnovationBlueprint;
